import React, { createContext, useContext, useEffect, useState } from 'react';

export type StudentTheme = 'light' | 'dark';

interface StudentThemeContextValue {
    theme: StudentTheme;
    toggleTheme: () => void;
}

const STORAGE_KEY = 'studentPortalTheme';

const StudentThemeContext = createContext<StudentThemeContextValue | undefined>(undefined);

function getInitialTheme(): StudentTheme {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
}

/**
 * Theme provider scoped to the Student Portal only — intentionally separate from
 * the app-wide AppContext so this can never affect the Admin Panel or Auth pages.
 */
export function StudentThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setTheme] = useState<StudentTheme>(getInitialTheme);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, theme);
    }, [theme]);

    const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

    return (
        <StudentThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </StudentThemeContext.Provider>
    );
}

export function useStudentTheme(): StudentThemeContextValue {
    const context = useContext(StudentThemeContext);
    if (!context) {
        throw new Error('useStudentTheme must be used within a StudentThemeProvider');
    }
    return context;
}
